/**
 * Attentive Back in Stock helpers for PIV2 PDPs.
 */
(function (global) {
  'use strict';

  const READY_EVENT = 'attentive-waitlist:button-ready';
  const STATE_EVENT = 'attentive-waitlist:state';
  const TRIGGER_SELECTOR = '[data-attentive-waitlist]';
  const BUTTON_ID = 'attn-waitlist-trigger-btn';
  const ELIGIBILITY_ID = 'attn-waitlist-eligibility';
  const NUDGE_DELAYS_MS = [0, 250, 600, 1200];
  const INITIAL_NUDGE_DELAYS_MS = [0, 250, 600, 1200, 2500, 4500, 7000];
  const INJECTION_TIMEOUT_MS = 4500;
  const INITIAL_INJECTION_TIMEOUT_MS = 10000;
  const VARIANT_SYNC_DELAY_MS = 750;

  /** @type {MutationObserver | null} */
  let domObserver = null;
  let listenersBound = false;
  let initialized = false;
  let attemptGeneration = 0;
  /** @type {WeakMap<HTMLElement, { variantId: string, status: 'loading' | 'ready' | 'failed', generation: number, timers: number[] }>} */
  const attempts = new WeakMap();
  /** @type {WeakSet<HTMLElement>} */
  const settledRoots = new WeakSet();

  /**
   * @param {ParentNode | Document | null | undefined} root
   * @returns {HTMLElement | null}
   */
  function getProductGridRoot(root = document) {
    if (root instanceof HTMLElement) {
      return root.closest('[data-product-grid-content]') ?? root;
    }
    return document.querySelector('[data-product-grid-content]');
  }

  /**
   * @param {ParentNode | Document | null | undefined} root
   * @returns {HTMLElement | null}
   */
  function getEligibilityScript(root = document) {
    if (root instanceof HTMLElement) {
      const scoped = root.querySelector(`#${ELIGIBILITY_ID}`);
      if (scoped instanceof HTMLElement) return scoped;
    }
    const globalScript = document.getElementById(ELIGIBILITY_ID);
    return globalScript instanceof HTMLElement ? globalScript : null;
  }

  /**
   * @param {string | null | undefined} variantId
   * @param {ParentNode | null | undefined} root
   */
  function syncEligibility(variantId, root) {
    if (!variantId) return;
    const eligScript = getEligibilityScript(root ?? document);
    if (!eligScript) return;
    try {
      const elig = JSON.parse(eligScript.textContent || '{}');
      if (!elig[variantId]) {
        elig[variantId] = true;
        eligScript.textContent = JSON.stringify(elig);
      }
    } catch (e) {
      // malformed JSON — leave as-is
    }
  }

  /**
   * @param {ParentNode | Document} root
   * @returns {HTMLInputElement | null}
   */
  function getVariantInput(root = document) {
    const scoped =
      root instanceof Document
        ? root.querySelector('[data-piv2-variant-id]')
        : root.querySelector?.('[data-piv2-variant-id]');
    if (scoped instanceof HTMLInputElement) return scoped;
    const fallback = document.querySelector('[data-piv2-variant-id]');
    return fallback instanceof HTMLInputElement ? fallback : null;
  }

  /**
   * @param {string} variantId
   * @param {ParentNode | null | undefined} root
   */
  function syncAllVariantIdInputs(variantId, root) {
    if (!variantId) return;
    const grid = getProductGridRoot(root ?? document);
    if (!(grid instanceof HTMLElement)) return;

    grid.querySelectorAll('input[name="id"]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      if (input.value === variantId) return;
      input.value = variantId;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /**
   * @param {HTMLElement | null | undefined} buyButtonsRoot
   * @returns {HTMLButtonElement | null}
   */
  function getWaitlistButton(buyButtonsRoot) {
    if (buyButtonsRoot instanceof HTMLElement) {
      const scoped = buyButtonsRoot.querySelector(`#${BUTTON_ID}`);
      if (scoped instanceof HTMLButtonElement && scoped.isConnected) return scoped;
      return null;
    }

    const globalBtn = document.getElementById(BUTTON_ID);
    if (globalBtn instanceof HTMLButtonElement && globalBtn.isConnected) return globalBtn;
    return null;
  }

  /**
   * @param {{ timers: number[] } | null | undefined} attempt
   */
  function clearAttemptTimers(attempt) {
    attempt?.timers.forEach((timer) => global.clearTimeout(timer));
    if (attempt) attempt.timers = [];
  }

  /**
   * @param {HTMLElement} root
   * @param {'loading' | 'ready' | 'failed'} status
   * @param {string} variantId
   * @param {string} [reason]
   */
  function emitState(root, status, variantId, reason = '') {
    root.dataset.piv2AttentiveBisState = status;
    root.dispatchEvent(
      new CustomEvent(STATE_EVENT, {
        bubbles: true,
        detail: { root, status, variantId, reason },
      })
    );
  }

  /**
   * @param {HTMLElement} root
   * @param {{ destroy?: boolean }} [options]
   */
  function cancel(root, options = {}) {
    const attempt = attempts.get(root);
    clearAttemptTimers(attempt);
    attempts.delete(root);
    delete root.dataset.piv2AttentiveBisState;

    if (options.destroy !== true) return;

    settledRoots.delete(root);
    delete root.dataset.piv2AttentiveVariantId;
    const trigger = root.querySelector(TRIGGER_SELECTOR);
    if (trigger instanceof HTMLElement && trigger.childNodes.length > 0) {
      trigger.replaceChildren();
    }
  }

  /**
   * @param {HTMLElement} root
   * @param {string} variantId
   * @param {boolean} [notify]
   * @returns {'loading'}
   */
  function startAttempt(root, variantId, notify = false) {
    const previous = attempts.get(root);
    clearAttemptTimers(previous);

    const generation = ++attemptGeneration;
    const attempt = {
      variantId,
      status: /** @type {'loading'} */ ('loading'),
      generation,
      timers: /** @type {number[]} */ ([]),
    };
    attempts.set(root, attempt);
    root.dataset.piv2AttentiveBisState = 'loading';
    if (notify) emitState(root, 'loading', variantId);

    const isInitialAttempt = !settledRoots.has(root);
    const nudgeDelays = isInitialAttempt ? INITIAL_NUDGE_DELAYS_MS : NUDGE_DELAYS_MS;
    const timeoutMs = isInitialAttempt ? INITIAL_INJECTION_TIMEOUT_MS : INJECTION_TIMEOUT_MS;

    nudgeDelays.forEach((delay) => {
      const timer = global.setTimeout(() => {
        const current = attempts.get(root);
        if (current?.generation !== generation || current.status !== 'loading') return;
        runAttentiveNudgePass(root);
      }, delay);
      attempt.timers.push(timer);
    });

    const timeout = global.setTimeout(() => {
      const current = attempts.get(root);
      if (current?.generation !== generation || current.status !== 'loading') return;
      current.status = 'failed';
      settledRoots.add(root);
      clearAttemptTimers(current);
      emitState(root, 'failed', variantId, 'timeout');
    }, timeoutMs);
    attempt.timers.push(timeout);

    return 'loading';
  }

  /**
   * @param {HTMLElement} root
   * @param {string} variantId
   * @param {HTMLButtonElement} button
   * @returns {'ready'}
   */
  function markReady(root, variantId, button) {
    const previous = attempts.get(root);
    clearAttemptTimers(previous);
    decorateWaitlistButton(button);
    attempts.set(root, {
      variantId,
      status: /** @type {'ready'} */ ('ready'),
      generation: ++attemptGeneration,
      timers: /** @type {number[]} */ ([]),
    });
    settledRoots.add(root);
    root.dataset.piv2AttentiveBisState = 'ready';
    root.dataset.piv2AttentiveVariantId = variantId;
    return 'ready';
  }

  /**
   * @param {HTMLElement} root
   * @param {string} variantId
   * @param {HTMLButtonElement} button
   */
  function publishReady(root, variantId, button) {
    markReady(root, variantId, button);
    emitState(root, 'ready', variantId);
  }

  /**
   * @param {HTMLElement} root
   * @param {string} variantId
   * @returns {'loading'}
   */
  function startVariantSync(root, variantId) {
    const previous = attempts.get(root);
    clearAttemptTimers(previous);

    const generation = ++attemptGeneration;
    const attempt = {
      variantId,
      status: /** @type {'loading'} */ ('loading'),
      generation,
      timers: /** @type {number[]} */ ([]),
    };
    attempts.set(root, attempt);
    root.dataset.piv2AttentiveBisState = 'loading';

    const timer = global.setTimeout(() => {
      const current = attempts.get(root);
      if (current?.generation !== generation || current.status !== 'loading') return;

      const button = getWaitlistButton(root);
      if (!button) {
        startAttempt(root, variantId, true);
        return;
      }

      publishReady(root, variantId, button);
    }, VARIANT_SYNC_DELAY_MS);
    attempt.timers.push(timer);

    return 'loading';
  }

  /**
   * @param {string | null | undefined} variantId
   * @param {HTMLElement | null | undefined} root
   * @returns {'loading' | 'ready' | 'failed'}
   */
  function request(variantId, root) {
    if (!variantId || !(root instanceof HTMLElement)) return 'failed';

    const id = String(variantId);
    const previous = attempts.get(root);
    const button = getWaitlistButton(root);
    const buttonVariantId = root.dataset.piv2AttentiveVariantId;

    if (previous?.variantId === id) {
      if (previous.status === 'failed') return 'failed';
      if (previous.status === 'loading') return 'loading';
      if (button) return markReady(root, id, button);
      if (previous.status === 'ready') return startAttempt(root, id);
      return previous.status;
    }

    clearAttemptTimers(previous);
    syncEligibility(id, root);
    syncAllVariantIdInputs(id, root);

    if (button) {
      if (!buttonVariantId || buttonVariantId === id) {
        return markReady(root, id, button);
      }

      fireAttentiveProductView(root);
      return startVariantSync(root, id);
    }

    return startAttempt(root, id);
  }

  /**
   * @param {HTMLButtonElement} button
   */
  function decorateWaitlistButton(button) {
    button.classList.add('add-to-cart-button', 'buy-buttons-v2__submit');
  }

  /**
   * @param {HTMLElement} trigger
   */
  function notifyIfButtonReady(trigger) {
    const button = trigger.querySelector(`#${BUTTON_ID}`);
    const root = trigger.closest('[data-buy-buttons-v2]');

    if (!(button instanceof HTMLButtonElement)) {
      if (root instanceof HTMLElement) {
        const attempt = attempts.get(root);
        if (attempt?.status === 'ready') {
          startAttempt(root, attempt.variantId, true);
        }
      }
      return;
    }
    if (button.dataset.attentiveBisReady === 'true') {
      if (root instanceof HTMLElement) {
        const variantInput = root.querySelector('[data-piv2-variant-id]');
        const variantId = variantInput instanceof HTMLInputElement ? variantInput.value : '';
        const attempt = attempts.get(root);
        if (variantId && attempt?.variantId === variantId && attempt.status === 'loading') {
          publishReady(root, variantId, button);
        }
      }
      return;
    }

    button.dataset.attentiveBisReady = 'true';
    decorateWaitlistButton(button);

    const variantInput = root?.querySelector('[data-piv2-variant-id]');
    const variantId = variantInput instanceof HTMLInputElement ? variantInput.value : '';
    const attempt = root instanceof HTMLElement ? attempts.get(root) : null;

    if (root instanceof HTMLElement && attempt?.variantId === variantId && attempt.status !== 'failed') {
      publishReady(root, variantId, button);
    }

    trigger.dispatchEvent(
      new CustomEvent(READY_EVENT, {
        bubbles: true,
        detail: { root, variantId },
      })
    );
  }

  /**
   * @param {HTMLElement} trigger
   */
  function observeTrigger(trigger) {
    if (trigger.dataset.attentiveBisObserved === 'true') {
      notifyIfButtonReady(trigger);
      return;
    }

    trigger.dataset.attentiveBisObserved = 'true';
    notifyIfButtonReady(trigger);

    const observer = new MutationObserver(() => {
      notifyIfButtonReady(trigger);
    });
    observer.observe(trigger, { childList: true, subtree: true });
  }

  function scanTriggers() {
    document.querySelectorAll(TRIGGER_SELECTOR).forEach((trigger) => {
      if (trigger instanceof HTMLElement) observeTrigger(trigger);
    });
  }

  /**
   * Reads the morphed picker's SSR variant payload.
   * @param {HTMLElement} picker
   * @returns {Array<Record<string, any>>}
   */
  function getPickerVariants(picker) {
    const script = picker.querySelector('[data-piv2-variants]');
    if (!script) return [];
    try {
      const parsed = JSON.parse(script.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  /**
   * @param {ParentNode | null | undefined} root
   */
  function fireAttentiveProductView(root) {
    const grid = getProductGridRoot(root ?? document);
    const picker = grid?.querySelector('variant-picker-v2');
    if (!(picker instanceof HTMLElement)) return;

    const productId = picker.dataset.productId;
    const variantInput = getVariantInput(grid ?? document);
    const variantId = variantInput?.value;
    if (!productId || !variantId) return;

    const attentive = global.attentive;
    const productViewFn =
      attentive?.analytics?.productView ??
      attentive?.productView ??
      null;

    if (typeof productViewFn !== 'function') return;

    const variants = getPickerVariants(picker);
    const variant = variants.find((v) => String(v.id) === String(variantId));
    const name =
      variant?.name ||
      variant?.title ||
      document.querySelector('.product-information-v2 h1')?.textContent?.trim() ||
      picker.dataset.productHandle ||
      '';

    try {
      productViewFn.call(attentive?.analytics ?? attentive, {
        items: [
          {
            productId: String(productId),
            productVariantId: String(variantId),
            name,
          },
        ],
      });
    } catch (e) {
      // Attentive tag rejected the productView payload — leave as-is
    }
  }

  /**
   * @param {ParentNode | null | undefined} root
   */
  function syncShopifyAnalyticsProduct(root) {
    const grid = getProductGridRoot(root ?? document);
    const picker = grid?.querySelector('variant-picker-v2');
    if (!(picker instanceof HTMLElement)) return;

    const productId = picker.dataset.productId;
    if (!productId) return;

    const analytics = global.ShopifyAnalytics?.meta;
    if (!analytics) return;

    analytics.product = analytics.product || {};
    analytics.product.id = Number(productId);
    analytics.product.gid = `gid://shopify/Product/${productId}`;

    const variants = getPickerVariants(picker);
    if (variants.length) {
      analytics.product.variants = variants.map((v) => ({
        id: Number(v.id),
        price: typeof v.price === 'number' ? v.price : undefined,
        name: v.name || v.title || '',
        public_title: v.public_title ?? v.title ?? '',
        sku: v.sku || '',
      }));
    }

    if (analytics.page && typeof analytics.page === 'object') {
      analytics.page.resourceId = Number(productId);
    }
  }

  /**
   * @param {ParentNode | null | undefined} root
   */
  function nudgeAttentiveVariantSync(root) {
    const buyButtons = root instanceof HTMLElement ? root.closest('[data-buy-buttons-v2]') : null;
    const scope = buyButtons ?? getProductGridRoot(root ?? document) ?? document;
    const variantInput = getVariantInput(scope);

    if (!variantInput?.value) return;

    syncShopifyAnalyticsProduct(scope);
    syncEligibility(variantInput.value, scope instanceof HTMLElement ? scope : document);
    syncAllVariantIdInputs(variantInput.value, scope);

    variantInput.dispatchEvent(new Event('input', { bubbles: true }));
    variantInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * @param {ParentNode | null | undefined} root
   */
  function runAttentiveNudgePass(root) {
    scanTriggers();
    nudgeAttentiveVariantSync(root ?? document);
    fireAttentiveProductView(root ?? document);
  }

  function bindListeners() {
    if (listenersBound) return;
    listenersBound = true;

    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const button = target.closest(`#${BUTTON_ID}`);
        const trigger = target.closest(TRIGGER_SELECTOR);
        if (!button && !trigger) return;

        const buyButtons = target.closest('[data-buy-buttons-v2]');
        if (buyButtons instanceof HTMLElement && buyButtons.dataset.piv2WaitlistActive !== 'true') {
          return;
        }

        const variantInput = getVariantInput(buyButtons ?? document);
        if (variantInput?.value) {
          syncEligibility(variantInput.value, buyButtons ?? document);
          syncAllVariantIdInputs(variantInput.value, buyButtons ?? document);
        }
      },
      true
    );

    document.addEventListener('piv2:pdp-sibling-hydrate-start', (event) => {
      const target = event.target;
      const scope =
        target instanceof HTMLElement
          ? target.closest('.shopify-section') ?? target
          : document;
      scope.querySelectorAll?.('[data-buy-buttons-v2]').forEach((root) => {
        if (root instanceof HTMLElement) cancel(root, { destroy: true });
      });
    });

    document.addEventListener('piv2:pdp-sibling-hydrate-complete', (event) => {
      const target = event.target;
      const scope =
        target instanceof HTMLElement
          ? target.closest('.shopify-section') ?? target
          : document;
      scanTriggers();
      nudgeAttentiveVariantSync(scope);
    });

    document.addEventListener('variant-picker:state-sync', () => {
      const buyButtons = document.querySelector('[data-buy-buttons-v2][data-piv2-waitlist-active="true"]');
      if (!buyButtons) return;
      const variantInput = getVariantInput(buyButtons);
      if (variantInput?.value) {
        syncEligibility(variantInput.value, buyButtons);
        syncAllVariantIdInputs(variantInput.value, buyButtons);
      }
    });
  }

  function ensureDomObserver() {
    if (domObserver) return;
    domObserver = new MutationObserver(() => {
      scanTriggers();
    });
    domObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  /**
   * @param {ParentNode | null | undefined} [root]
   */
  function refresh(root) {
    const scope = root ?? document;
    NUDGE_DELAYS_MS.forEach((delay) => {
      global.setTimeout(() => {
        runAttentiveNudgePass(scope);
      }, delay);
    });
  }

  function init() {
    if (initialized) {
      refresh();
      return;
    }
    initialized = true;

    bindListeners();
    ensureDomObserver();
    refresh();
    document.dispatchEvent(new CustomEvent('attentive-waitlist:initialized'));
  }

  global.AttentiveBackInStock = {
    syncEligibility,
    syncAllVariantIdInputs,
    refresh,
    getWaitlistButton,
    fireAttentiveProductView,
    request,
    cancel,
    init,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})(typeof globalThis !== 'undefined' ? globalThis : window);
